import React, { useState, useEffect, useCallback, useContext, useRef, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';
import { API, AuthContext } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  FileText,
  Package,
  Wrench,
  Check,
  X,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  Plus,
  Minus,
  RefreshCw,
  Search,
  CheckCircle2,
  XCircle,
  ArrowRightLeft,
  Building,
  Camera,
  Flashlight,
  ZoomIn,
  Loader2,
  Trash2,
  Users,
  Crown,
  UserCheck,
  Eye
} from 'lucide-react';

// ==========================================
// FULLSCREEN BARCODE SCANNER COMPONENT
// ==========================================
const FullscreenScanner = ({ onScan, onClose }) => {
  const html5Ref = useRef(null);
  const [torchOn, setTorchOn] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [error, setError] = useState(null);
  const containerId = 'fsm-barcode-scanner';

  useEffect(() => {
    let mounted = true;

    const startScanner = async () => {
      try {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const stream = await navigator.mediaDevices.getUserMedia({
              video: { facingMode: 'environment' }
            });
            stream.getTracks().forEach(t => t.stop());
          } catch (err) {
            console.warn('Camera permission check failed:', err);
          }
        }

        const scanner = new Html5Qrcode(containerId, {
          verbose: false,
          experimentalFeatures: { useBarCodeDetectorIfSupported: true }
        });

        html5Ref.current = scanner;
        
        await scanner.start(
          { facingMode: 'environment' },
          {
            fps: 15,
            qrbox: { width: 280, height: 160 },
            aspectRatio: 1.777,
            disableFlip: false,
          },
          (decodedText) => {
            if (mounted) {
              onScan(decodedText);
            }
          },
          () => {}
        );
      } catch (err) {
        console.error('Scanner start error:', err);
        if (mounted) {
          setError('Não foi possível aceder à câmara. Verifique as permissões.');
        }
      }
    };

    startScanner();

    return () => {
      mounted = false;
      if (html5Ref.current) {
        html5Ref.current.stop().catch(() => {});
      }
    };
  }, [onScan]);

  const toggleTorch = async () => {
    if (!html5Ref.current) return;
    try {
      const videoElement = document.querySelector(`#${containerId} video`);
      if (videoElement && videoElement.srcObject) {
        const track = videoElement.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.torch) {
          await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
          setTorchOn(!torchOn);
        }
      }
    } catch (err) {
      console.warn('Torch toggle failed:', err);
    }
  };

  const handleZoom = async () => {
    if (!html5Ref.current) return;
    try {
      const videoElement = document.querySelector(`#${containerId} video`);
      if (videoElement && videoElement.srcObject) {
        const track = videoElement.srcObject.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        if (capabilities.zoom) {
          const newZoom = zoom >= capabilities.zoom.max ? capabilities.zoom.min : zoom + 1;
          await track.applyConstraints({ advanced: [{ zoom: newZoom }] });
          setZoom(newZoom);
        }
      }
    } catch (err) {
      console.warn('Zoom failed:', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      <div className="flex justify-between items-center p-4 bg-black/50">
        <h2 className="text-white font-medium">Ler Código de Barras</h2>
        <button
          onClick={onClose}
          className="text-white p-2 hover:bg-white/10 rounded-full"
        >
          <X className="w-6 h-6" />
        </button>
      </div>
      
      <div className="flex-1 relative">
        {error ? (
          <div className="flex items-center justify-center h-full text-white p-4 text-center">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div id={containerId} className="w-full h-full" />
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-72 h-40 border-2 border-white/50 rounded-lg">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-blue-500 rounded-tl-lg" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-blue-500 rounded-tr-lg" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-blue-500 rounded-bl-lg" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-blue-500 rounded-br-lg" />
              </div>
            </div>
          </>
        )}
      </div>
      
      <div className="p-4 bg-black/50 flex justify-center gap-6">
        <button
          onClick={toggleTorch}
          className={`p-3 rounded-full ${torchOn ? 'bg-yellow-500' : 'bg-white/20'}`}
        >
          <Flashlight className="w-6 h-6 text-white" />
        </button>
        <button
          onClick={handleZoom}
          className="p-3 rounded-full bg-white/20"
        >
          <ZoomIn className="w-6 h-6 text-white" />
        </button>
      </div>
    </div>
  );
};

// ==========================================
// FSM SERVICE APPOINTMENTS - MY SERVICES
// ==========================================

// Planned items already confirmed (technician-added extras don't count as a confirmation)
const hasPlannedConfirmation = (apt) =>
  [...(apt.actual_services || []), ...(apt.actual_parts || [])].some(i => i.source !== 'technician_added');

// Checkbox/quantity state for planned items, mirroring what is already confirmed
const buildSelectionState = (data) => {
  const services = {}, parts = {}, svcQty = {}, partQty = {};
  const confirmed = hasPlannedConfirmation(data);
  (data.planned_services || []).forEach(s => {
    const existing = (data.actual_services || []).find(a => a.source_fsm_service_line_item_id === s.fsm_service_line_item_id);
    services[s.fsm_service_line_item_id] = confirmed ? !!existing : true;
    svcQty[s.fsm_service_line_item_id] = existing?.quantity ?? s.quantity;
  });
  (data.planned_parts || []).forEach(p => {
    const existing = (data.actual_parts || []).find(a => a.source_fsm_part_line_item_id === p.fsm_part_line_item_id);
    parts[p.fsm_part_line_item_id] = existing ? existing.quantity > 0 : p.match_status === 'matched';
    partQty[p.fsm_part_line_item_id] = existing ? existing.quantity : p.quantity;
  });
  return { services, parts, svcQty, partQty };
};

const PUSH_LABELS = {
  synced: ['Zoho FSM atualizado', 'text-green-700'],
  no_changes: ['Zoho FSM já estava igual', 'text-green-700'],
  error: ['Zoho FSM não atualizado', 'text-red-600'],
  not_authorized: ['Zoho FSM sem autorização', 'text-amber-600'],
  skipped: ['Sem Ordem de Trabalho Zoho', 'text-gray-500'],
};

// Result of sending the closed appointment to Zoho FSM (+ admin re-send)
const FsmPushStatus = ({ apt, isAdmin, onRepush }) => {
  const [busy, setBusy] = useState(false);
  const push = apt.fsm_push;
  if (!push && !isAdmin) return null;
  const [label, color] = PUSH_LABELS[push?.status] || ['Ainda não enviado ao Zoho FSM', 'text-gray-500'];
  return (
    <div className="mt-3 pt-3 border-t text-sm space-y-1" data-testid="fsm-push-status">
      <div className="flex items-center justify-between gap-2">
        <span className={`font-medium ${color}`}>{label}</span>
        {isAdmin && (
          <Button variant="outline" size="sm" disabled={busy} data-testid="fsm-repush-btn"
            onClick={async () => { setBusy(true); await onRepush(); setBusy(false); }}>
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Reenviar para Zoho
          </Button>
        )}
      </div>
      {push?.error && <p className="text-xs text-red-600">{push.error}</p>}
      {(push?.warnings || []).map((w) => <p key={w} className="text-xs text-amber-700">{w}</p>)}
    </div>
  );
};

// Technician-added service/part: quantity +/- and remove (price is fixed by the catalog)
const TechItemRow = ({ item, isEditable, onQty, onRemove }) => (
  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200 mb-2" data-testid={`tech-item-${item.id}`}>
    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium truncate">{item.name}</div>
      <div className="text-xs text-gray-500">{item.stockflow_sku}</div>
    </div>
    <div className="text-right flex-shrink-0">
      {isEditable ? (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={item.quantity <= 1}
            onClick={() => onQty(item.id, item.quantity - 1)} data-testid={`tech-item-minus-${item.id}`}>
            <Minus className="w-3 h-3" />
          </Button>
          <span className="w-8 text-center text-sm font-medium" data-testid={`tech-item-qty-${item.id}`}>{item.quantity}</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
            onClick={() => onQty(item.id, item.quantity + 1)} data-testid={`tech-item-plus-${item.id}`}>
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <span className="text-sm text-gray-600">×{item.quantity}</span>
      )}
      <div className="text-sm font-medium text-blue-700">
        €{(item.amount || (item.unit_price * item.quantity) || 0).toFixed(2)}
      </div>
    </div>
    {isEditable && (
      <Button variant="ghost" size="sm" title="Remover"
        className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
        onClick={() => onRemove(item.id)} data-testid={`tech-item-remove-${item.id}`}>
        <Trash2 className="w-4 h-4" />
      </Button>
    )}
  </div>
);

const FSMServiceAppointments = () => {
  const { user, token } = useContext(AuthContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [view, setView] = useState('list'); // list, detail
  const [filter, setFilter] = useState('today'); // today, day_1, day_2, day_3, day_4, day_5, closed, all
  const [syncing, setSyncing] = useState(false);
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const [deepLinkError, setDeepLinkError] = useState(null);
  
  // Return context - where to go when clicking "Voltar" button
  const [returnTo, setReturnTo] = useState(null);
  
  // Helper: Format abbreviated date (e.g., "23 Set")
  const formatShortDate = useCallback((date) => {
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return `${date.getDate()} ${months[date.getMonth()]}`;
  }, []);
  
  // Helper: Get date string for API (YYYY-MM-DD) in LOCAL timezone (not UTC)
  const formatApiDate = useCallback((date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);
  
  // State for tab counts
  const [tabCounts, setTabCounts] = useState({});
  
  // Generate dynamic tabs: today + next 4 days (5 total) + Concluídos
  const tabs = useMemo(() => {
    const result = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const shortDate = (d) => `${d.getDate()} ${months[d.getMonth()]}`;
    // Use local date format, not UTC
    const apiDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    // Today tab
    result.push({
      id: 'today',
      label: `Hoje`,
      shortLabel: shortDate(today),
      date: apiDate(today)
    });
    
    // Next 4 days (total 5 days including today)
    for (let i = 1; i <= 4; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      result.push({
        id: `day_${i}`,
        label: shortDate(date),
        shortLabel: shortDate(date),
        date: apiDate(date)
      });
    }
    
    // "Abertos" tab to see only open APs
    result.push({ id: 'open', label: 'Abertos', shortLabel: 'Abertos', date: null, status: 'open' });
    
    // "Todos" tab to see all APs
    result.push({ id: 'all', label: 'Todos', shortLabel: 'Todos', date: null });
    
    return result;
  }, []); // Empty deps - tabs only change when day changes (page reload)
  
  // Search filter state for AP list
  const [apSearchQuery, setApSearchQuery] = useState('');
  
  // Confirmation state
  const [confirmingItems, setConfirmingItems] = useState(false);
  const [selectedServices, setSelectedServices] = useState({});
  const [selectedParts, setSelectedParts] = useState({});
  const [serviceQuantities, setServiceQuantities] = useState({});
  const [partQuantities, setPartQuantities] = useState({});

  const applySelectionState = useCallback((data) => {
    const { services, parts, svcQty, partQty } = buildSelectionState(data);
    setSelectedServices(services);
    setSelectedParts(parts);
    setServiceQuantities(svcQty);
    setPartQuantities(partQty);
  }, []);
  
  // Add item dialog
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [addItemType, setAddItemType] = useState('part'); // part, service
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Barcode scanner
  const [showScanner, setShowScanner] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);

  // ============================================================
  // HELPER: Calculate effective unit price (preserving discount)
  // ============================================================
  const getEffectiveUnitPrice = (item) => {
    const qty = item.quantity || 1;
    const amount = item.amount || 0;
    if (qty > 0 && amount > 0) {
      return amount / qty;
    }
    return item.list_price || item.unit_price || 0;
  };

  // HELPER: Calculate line amount based on current quantity
  const calculateLineAmount = (item, currentQty) => {
    const unitPrice = getEffectiveUnitPrice(item);
    return unitPrice * currentQty;
  };

  // HELPER: Calculate total for selected services with current quantities
  const calculateSelectedServicesTotal = () => {
    if (!selectedAppointment?.planned_services) return 0;
    
    return selectedAppointment.planned_services.reduce((total, service) => {
      const id = service.fsm_service_line_item_id;
      if (selectedServices[id]) {
        const qty = serviceQuantities[id] || service.quantity || 1;
        const lineAmount = calculateLineAmount(service, qty);
        return total + lineAmount;
      }
      return total;
    }, 0);
  };

  // HELPER: Calculate total for selected parts with current quantities  
  const calculateSelectedPartsTotal = () => {
    if (!selectedAppointment?.planned_parts) return 0;
    
    return selectedAppointment.planned_parts.reduce((total, part) => {
      const id = part.fsm_part_line_item_id;
      if (selectedParts[id]) {
        const qty = partQuantities[id] || part.quantity || 1;
        const lineAmount = calculateLineAmount(part, qty);
        return total + lineAmount;
      }
      return total;
    }, 0);
  };

  // HELPER: Calculate dynamic "Total a Confirmar" based on selections
  const calculatePendingTotal = () => {
    return calculateSelectedServicesTotal() + calculateSelectedPartsTotal();
  };

  // Fetch appointments
  const fetchAppointments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      let endpoint = `${API}/fsm/appointments/my`;
      const params = new URLSearchParams();
      
      // Find the current tab to get its date
      const currentTab = tabs.find(t => t.id === filter);
      
      if (filter === 'today') {
        endpoint = `${API}/fsm/appointments/today`;
      } else if (filter === 'open') {
        // Only open (not closed) appointments
        params.append('status', 'open');
        endpoint = `${API}/fsm/appointments/my?${params.toString()}`;
      } else if (filter === 'all') {
        // All appointments - no date filter
        endpoint = `${API}/fsm/appointments/my`;
      } else if (currentTab?.date) {
        // Specific day filter (day_1, day_2, etc.)
        params.append('date_from', currentTab.date);
        params.append('date_to', currentTab.date);
        endpoint = `${API}/fsm/appointments/my?${params.toString()}`;
      }
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const items = response.data.items || [];
      
      if (filter === 'today') {
        setTodayAppointments(items);
      }
      setAppointments(items);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Erro ao carregar serviços');
    } finally {
      setLoading(false);
    }
  }, [token, filter, tabs]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  // Fetch counts for all date tabs (in parallel for efficiency)
  const fetchTabCounts = useCallback(async () => {
    if (!token) return;
    
    try {
      const countPromises = tabs
        .filter(tab => tab.date) // Only tabs with dates
        .map(async (tab) => {
          try {
            const params = new URLSearchParams();
            params.append('date_from', tab.date);
            params.append('date_to', tab.date);
            
            const response = await axios.get(`${API}/fsm/appointments/my?${params.toString()}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            // Use 'total' from response or count items
            return { id: tab.id, count: response.data.total ?? response.data.items?.length ?? 0 };
          } catch {
            return { id: tab.id, count: 0 };
          }
        });
      
      const results = await Promise.all(countPromises);
      const counts = {};
      results.forEach(r => { counts[r.id] = r.count; });
      setTabCounts(counts);
    } catch (error) {
      console.error('Error fetching tab counts:', error);
    }
  }, [token, tabs]);

  useEffect(() => {
    fetchTabCounts();
  }, [fetchTabCounts]);

  // ============================================================
  // DEEP-LINK: Open AP directly by ID from URL parameter ?id=X
  // ============================================================
  const openAppointmentById = useCallback(async (appointmentId) => {
    if (!token || !appointmentId) return;
    
    setDeepLinkLoading(true);
    setDeepLinkError(null);
    
    try {
      const response = await axios.get(`${API}/fsm/appointments/${appointmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      setSelectedAppointment(data);
      applySelectionState(data);
      
      setView('detail');
      
      // Clear the URL parameter after successful load (keep clean URL)
      setSearchParams({}, { replace: true });
      
    } catch (error) {
      console.error('Error fetching appointment by ID:', error);
      if (error.response?.status === 403) {
        setDeepLinkError('Sem autorização para visualizar esta AP');
        toast.error('Sem autorização para visualizar esta AP');
      } else if (error.response?.status === 404) {
        setDeepLinkError('AP não encontrada');
        toast.error('AP não encontrada');
      } else {
        setDeepLinkError('Erro ao carregar AP');
        toast.error('Erro ao carregar detalhes da AP');
      }
    } finally {
      setDeepLinkLoading(false);
    }
  }, [token, setSearchParams, applySelectionState]);

  // Process deep-link on mount and capture returnTo context
  useEffect(() => {
    const idParam = searchParams.get('id');
    if (idParam && token) {
      // Capture returnTo from location state (passed from Invoicing or other sources)
      const returnContext = location.state?.returnTo || null;
      setReturnTo(returnContext);
      openAppointmentById(idParam);
    }
  }, [searchParams, token, openAppointmentById, location.state]);

  // Open appointment detail
  const openAppointment = async (appointment) => {
    try {
      const response = await axios.get(`${API}/fsm/appointments/${appointment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAppointment(response.data);
      applySelectionState(response.data);
      
      setView('detail');
    } catch (error) {
      console.error('Error fetching appointment:', error);
      toast.error('Erro ao carregar detalhes do serviço');
    }
  };

  // Confirm all items
  const confirmAllItems = async () => {
    if (!selectedAppointment) return;
    
    setConfirmingItems(true);
    try {
      const removedServiceIds = Object.entries(selectedServices)
        .filter(([_, selected]) => !selected)
        .map(([id]) => id);
      
      const removedPartIds = Object.entries(selectedParts)
        .filter(([_, selected]) => !selected)
        .map(([id]) => id);
      
      // Build services array with current quantities (including modified ones)
      const servicesWithQty = Object.entries(selectedServices)
        .filter(([_, selected]) => selected)
        .map(([id]) => ({
          fsm_service_line_item_id: id,
          quantity: serviceQuantities[id] || 1
        }));
      
      // Build parts array with current quantities
      const partsWithQty = Object.entries(selectedParts)
        .filter(([_, selected]) => selected)
        .map(([id]) => ({
          fsm_part_line_item_id: id,
          quantity: partQuantities[id] || 1
        }));
      
      await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/confirm`,
        {
          confirm_all: true,
          removed_service_ids: removedServiceIds,
          removed_part_ids: removedPartIds,
          services: servicesWithQty,
          parts: partsWithQty
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Serviços e materiais confirmados');
      
      // Refresh appointment
      await openAppointment(selectedAppointment);
    } catch (error) {
      console.error('Error confirming items:', error);
      toast.error('Erro ao confirmar itens');
    } finally {
      setConfirmingItems(false);
    }
  };

  // Confirm selected items
  const confirmSelectedItems = async () => {
    if (!selectedAppointment) return;
    
    setConfirmingItems(true);
    try {
      const services = Object.entries(selectedServices)
        .filter(([_, selected]) => selected)
        .map(([id]) => ({
          fsm_service_line_item_id: id,
          quantity: serviceQuantities[id]
        }));
      
      // Include parts with quantity > 0 (used) OR quantity = 0 (explicitly not used)
      // Parts that are matched but not selected should be sent with quantity 0
      const parts = [];
      
      // First, add selected parts with their quantities
      Object.entries(selectedParts).forEach(([id, selected]) => {
        if (selected) {
          const qty = partQuantities[id] ?? 1;
          parts.push({
            fsm_part_line_item_id: id,
            quantity: qty
          });
        }
      });
      
      // Second, add unselected matched parts with quantity 0 (confirmed not used)
      if (selectedAppointment.planned_parts) {
        selectedAppointment.planned_parts.forEach(pp => {
          const id = pp.fsm_part_line_item_id;
          if (pp.match_status === 'matched' && pp.stockflow_sku) {
            // Only add if not already in parts array
            if (!parts.some(p => p.fsm_part_line_item_id === id)) {
              // Check if it was explicitly deselected OR has quantity 0
              const isNotSelected = selectedParts[id] === false;
              const hasZeroQty = partQuantities[id] === 0;
              
              if (isNotSelected || hasZeroQty) {
                parts.push({
                  fsm_part_line_item_id: id,
                  quantity: 0  // Confirmed not used
                });
              }
            }
          }
        });
      }
      
      const removedServiceIds = Object.entries(selectedServices)
        .filter(([_, selected]) => !selected)
        .map(([id]) => id);

      await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/confirm`,
        {
          confirm_all: false,
          services,
          parts,
          removed_service_ids: removedServiceIds
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Alterações guardadas');
      await openAppointment(selectedAppointment);
    } catch (error) {
      console.error('Error confirming items:', error);
      toast.error('Erro ao confirmar itens');
    } finally {
      setConfirmingItems(false);
    }
  };

  // Close appointment state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeData, setCloseData] = useState({
    payment_method: '',
    payment_status: 'pending',
    wants_invoice: false,
    client_nif: '',
    client_name: '',
    client_email: '',
    other_value: 0,
    other_description: '',
    notes: ''
  });
  const [closingAppointment, setClosingAppointment] = useState(false);
  
  // MB Receipt upload state (for close flow)
  const [closeMbReceiptFile, setCloseMbReceiptFile] = useState(null);
  const [closeMbReceiptPreview, setCloseMbReceiptPreview] = useState(null);
  const closeMbReceiptInputRef = useRef(null);
  
  // MB Receipt upload state (general)
  const [showMBReceiptDialog, setShowMBReceiptDialog] = useState(false);
  const [mbReceiptUploading, setMBReceiptUploading] = useState(false);
  const [mbReceiptPreview, setMBReceiptPreview] = useState(null);
  const mbReceiptInputRef = useRef(null);
  
  // Upload MB Receipt
  const uploadMBReceipt = async (file) => {
    if (!selectedAppointment || !file) return;
    
    setMBReceiptUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/mb-receipt`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(response.data.message || 'Talão MB anexado com sucesso');
      setShowMBReceiptDialog(false);
      setMBReceiptPreview(null);
      
      // Refresh appointment data
      const refreshed = await axios.get(
        `${API}/fsm/appointments/${selectedAppointment.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedAppointment(refreshed.data);
      
    } catch (err) {
      console.error('Error uploading MB receipt:', err);
      toast.error(err.response?.data?.detail || 'Erro ao anexar talão MB');
    } finally {
      setMBReceiptUploading(false);
    }
  };
  
  // Handle file selection for MB receipt
  const handleMBReceiptFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor seleciona uma imagem (JPEG ou PNG)');
      return;
    }
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setMBReceiptPreview({
        file,
        dataUrl: event.target.result
      });
    };
    reader.readAsDataURL(file);
  };
  
  // Delete MB Receipt
  const deleteMBReceipt = async () => {
    if (!selectedAppointment) return;
    
    if (!window.confirm('Tem a certeza que pretende eliminar o talão MB?')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/fsm/appointments/${selectedAppointment.id}/mb-receipt`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Talão MB eliminado');
      
      // Refresh appointment data
      const refreshed = await axios.get(
        `${API}/fsm/appointments/${selectedAppointment.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedAppointment(refreshed.data);
      
    } catch (err) {
      console.error('Error deleting MB receipt:', err);
      toast.error(err.response?.data?.detail || 'Erro ao eliminar talão MB');
    }
  };
  
  const closeAppointment = async () => {
    if (!selectedAppointment) return;
    setShowCloseDialog(true);
    
    // Pre-fill from FSM data if available
    // Safe normalization: only trim spaces, preserve original for validation
    // NIF comes from Zoho as a number
    const normalizedNif = String(selectedAppointment.client_nif ?? '').replace(/\s+/g, '');
    
    setCloseData(prev => ({
      ...prev,
      client_nif: normalizedNif,
      client_name: selectedAppointment.client_contact_name || selectedAppointment.client_company_name || '',
      client_email: selectedAppointment.client_email || ''
    }));
  };
  
  // Portuguese NIF validation
  // Only allows: digits, spaces (for formatting), and strips them safely
  const validatePortugueseNIF = (nif) => {
    const rawNif = (nif || '').trim();
    
    // Check for invalid characters (anything other than digits and spaces)
    if (/[^\d\s]/.test(rawNif)) {
      return { valid: false, error: 'O NIF deve ter 9 dígitos.' };
    }
    
    // Remove only spaces (safe normalization)
    const cleanNif = rawNif.replace(/\s/g, '');
    
    // Check length
    if (cleanNif.length !== 9) {
      return { valid: false, error: 'O NIF deve ter 9 dígitos.' };
    }
    
    // Checksum validation
    const multipliers = [9, 8, 7, 6, 5, 4, 3, 2];
    let total = 0;
    for (let i = 0; i < 8; i++) {
      total += parseInt(cleanNif[i]) * multipliers[i];
    }
    const remainder = total % 11;
    const checkDigit = remainder < 2 ? 0 : 11 - remainder;
    
    if (parseInt(cleanNif[8]) !== checkDigit) {
      return { valid: false, error: 'O NIF indicado não é válido. Confirma os dados de faturação.' };
    }
    
    return { valid: true, error: null, normalized: cleanNif };
  };
  
  const submitCloseAppointment = async () => {
    if (!selectedAppointment) return;
    
    // Validation
    if (closeData.wants_invoice) {
      const nifValidation = validatePortugueseNIF(closeData.client_nif);
      if (!nifValidation.valid) {
        toast.error(nifValidation.error);
        return;
      }
      if (!closeData.client_name || closeData.client_name.trim().length < 3) {
        toast.error('Indica o nome para a fatura');
        return;
      }
      if (!closeData.client_email || !closeData.client_email.includes('@')) {
        toast.error('Email inválido para envio da fatura');
        return;
      }
    }
    
    if (closeData.payment_status === 'paid' && !closeData.payment_method) {
      toast.error('Seleciona o método de pagamento');
      return;
    }
    
    setClosingAppointment(true);
    try {
      const response = await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/close`,
        closeData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Upload MB receipt if selected and payment method is card/MB
      if (closeMbReceiptFile && closeData.payment_method === 'card') {
        try {
          const formData = new FormData();
          formData.append('file', closeMbReceiptFile);
          await axios.post(
            `${API}/fsm/appointments/${selectedAppointment.id}/mb-receipt`,
            formData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
          toast.success('Talão MB anexado com sucesso');
        } catch (receiptError) {
          console.error('Error uploading MB receipt:', receiptError);
          toast.warning('Serviço fechado, mas erro ao anexar talão MB');
        }
      }
      
      toast.success(response.data.message || 'Serviço concluído e enviado para Faturação');
      
      if (response.data.sage_din_error) {
        toast.warning(`Aviso DIN SAGE: ${response.data.sage_din_error}`);
      }

      const push = response.data.fsm_push || {};
      if (push.status === 'synced') toast.success('Ordem de Trabalho atualizada no Zoho FSM');
      else if (push.status === 'error' || push.status === 'not_authorized') {
        toast.warning(`Zoho FSM não atualizado: ${push.error} — será repetido automaticamente`);
      }
      (push.warnings || []).forEach((w) => toast.warning(`Zoho FSM: ${w}`, { duration: 10000 }));
      
      // Reset MB receipt state
      setCloseMbReceiptFile(null);
      setCloseMbReceiptPreview(null);
      
      setShowCloseDialog(false);
      setView('list');
      setSelectedAppointment(null);
      fetchAppointments();
    } catch (error) {
      console.error('Error closing appointment:', error);
      const msg = error.response?.data?.detail || 'Erro ao fechar serviço';
      toast.error(msg);
    } finally {
      setClosingAppointment(false);
    }
  };

  const openAddItem = (type) => {
    setAddItemType(type);
    setSearchQuery('');
    setSearchResults([]);
    setShowAddItemDialog(true);
  };

  // Search products for adding items (parts: technician warehouse + available stock; services: SAGE catalog)
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    if (addItemType === 'service') {
      setSearchLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/products?search=${encodeURIComponent(query)}&article_type=service&page_size=50`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSearchResults(data.items || data.products || []);
      } catch (error) {
        toast.error('Erro na pesquisa de serviços');
      } finally {
        setSearchLoading(false);
      }
      return;
    }
    
    const userWarehouse = user?.reference_warehouse;
    if (!userWarehouse) {
      console.warn('User has no reference_warehouse set');
      toast.error('Armazém do técnico não configurado');
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(
        `${API}/products?search=${encodeURIComponent(query)}&warehouse=${userWarehouse}&hide_zero_stock=true&page_size=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.items || response.data.products || []);
    } catch (error) {
      console.error('Error searching products:', error);
      toast.error('Erro na pesquisa de produtos');
    } finally {
      setSearchLoading(false);
    }
  };

  // Add technician item
  const addTechnicianItem = async (product) => {
    if (!selectedAppointment) return;
    
    // Get the best price available: sale_price_with_vat > sale_price > unit_price > cost_price
    const unitPrice = product.sale_price_with_vat || product.sale_price || product.unit_price || product.cost_price || 0;
    
    try {
      await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/add-item`,
        {
          item_type: addItemType,
          stockflow_sku: product.sku,
          name: product.name,
          quantity: 1,
          unit_price: unitPrice
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${addItemType === 'service' ? 'Serviço' : 'Material'} adicionado: €${unitPrice.toFixed(2)}`);
      setShowAddItemDialog(false);
      setSearchQuery('');
      setSearchResults([]);
      await openAppointment(selectedAppointment);
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error(error.response?.data?.detail || `Erro ao adicionar ${addItemType === 'service' ? 'serviço' : 'material'}`);
    }
  };
  
  // Handle barcode scan
  const handleBarcodeScan = async (code) => {
    setShowScanner(false);
    setScannerLoading(true);
    
    const userWarehouse = user?.reference_warehouse;
    
    try {
      // Search for product by barcode/SKU
      const response = await axios.get(
        `${API}/products?search=${encodeURIComponent(code)}&warehouse=${userWarehouse}&hide_zero_stock=true&page_size=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const items = response.data.items || response.data.products || [];
      
      if (items.length > 0) {
        const product = items[0];
        await addTechnicianItem(product);
      } else {
        toast.error(`Produto não encontrado: ${code}`);
      }
    } catch (error) {
      console.error('Barcode search error:', error);
      toast.error('Erro ao procurar produto');
    } finally {
      setScannerLoading(false);
    }
  };
  
  const repushToFsm = async () => {
    try {
      const { data } = await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/push-to-fsm`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.status === 'synced' || data.status === 'no_changes') toast.success('Zoho FSM atualizado');
      else toast.warning(`Zoho FSM: ${data.error || data.status}`);
      await openAppointment(selectedAppointment);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reenviar para o Zoho');
    }
  };

  const updateTechnicianItemQty = async (itemId, quantity) => {
    if (!selectedAppointment || quantity < 1) return;
    try {
      await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/update-item-quantity`,
        { item_id: itemId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await openAppointment(selectedAppointment);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar quantidade');
    }
  };

  // Remove technician-added item
  const removeTechnicianItem = async (itemId) => {
    if (!selectedAppointment) return;
    
    try {
      await axios.post(
        `${API}/fsm/appointments/${selectedAppointment.id}/remove-item`,
        { item_id: itemId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Item removido');
      await openAppointment(selectedAppointment);
    } catch (error) {
      console.error('Error removing item:', error);
      const msg = error.response?.data?.detail || 'Erro ao remover material';
      toast.error(msg);
    }
  };

  // Trigger sync: admin = full incremental sync; others = re-sync own active services from Zoho
  const triggerSync = async () => {
    setSyncing(true);
    try {
      const isAdmin = user?.role === 'admin';
      const { data } = await axios.post(
        `${API}/fsm/appointments/${isAdmin ? 'sync/manual' : 'my/refresh'}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (isAdmin) toast.success('Sincronização iniciada');
      else if (data?.throttled) toast.info('Já atualizado há instantes');
      else toast.success(data?.synced ? `${data.synced} serviço(s) atualizado(s) do FSM` : 'Os seus serviços já estão atualizados');
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error(error.response?.data?.detail || 'Erro na sincronização');
    } finally {
      await fetchAppointments();
      fetchTabCounts();
      setSyncing(false);
    }
  };

  // Auto-refresh the list (local data only, no Zoho calls) every 2 min while visible, and on return to the app
  useEffect(() => {
    const reload = () => {
      if (document.visibilityState === 'visible' && !selectedAppointment) {
        fetchAppointments();
        fetchTabCounts();
      }
    };
    const timer = setInterval(reload, 120000);
    document.addEventListener('visibilitychange', reload);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', reload);
    };
  }, [fetchAppointments, fetchTabCounts, selectedAppointment]);

  // Format date/time
  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('pt-PT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Status badge
  const StatusBadge = ({ status }) => {
    const config = {
      open: { label: 'Aberto', className: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Em Curso', className: 'bg-yellow-100 text-yellow-800' },
      closed: { label: 'Fechado', className: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelado', className: 'bg-red-100 text-red-800' }
    };
    const { label, className } = config[status] || config.open;
    return <Badge className={className}>{label}</Badge>;
  };

  // Part match badge
  const MatchBadge = ({ status }) => {
    if (status === 'matched') {
      return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="w-3 h-3 mr-1" />SKU OK</Badge>;
    }
    if (status === 'unmatched') {
      return <Badge className="bg-orange-100 text-orange-800"><AlertTriangle className="w-3 h-3 mr-1" />SKU não encontrado</Badge>;
    }
    return <Badge className="bg-gray-100 text-gray-800"><XCircle className="w-3 h-3 mr-1" />Sem SKU</Badge>;
  };

  // ==========================================
  // RENDER LIST VIEW
  // ==========================================
  const renderListView = () => (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Os Meus Serviços</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={triggerSync}
          disabled={syncing}
          data-testid="fsm-my-services-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'A sincronizar...' : 'Actualizar'}
        </Button>
      </div>

      {/* Search filter */}
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Pesquisar AP, cliente, morada..."
          value={apSearchQuery}
          onChange={(e) => setApSearchQuery(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
          data-testid="search-ap-input"
        />
        {apSearchQuery && (
          <button
            onClick={() => setApSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <XCircle className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filter tabs - 5 days + Todos, optimized for mobile */}
      <div className="flex gap-1 border-b pb-2">
        {tabs.map(tab => {
          const count = tabCounts[tab.id];
          const hasCount = tab.date && count !== undefined && count > 0;
          const isActive = filter === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className={`flex-1 min-w-0 py-2 px-1 text-center rounded-t-lg transition-colors ${
                isActive 
                  ? 'bg-primary text-primary-foreground font-semibold' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <div className="text-xs sm:text-sm truncate">{tab.label}</div>
              {hasCount && (
                <div className={`text-[10px] font-bold mt-0.5 ${isActive ? 'text-primary-foreground' : 'text-blue-600'}`}>
                  {count}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Appointments list */}
      {(() => {
        // Filter appointments based on search query
        const filteredAppointments = apSearchQuery.trim() 
          ? appointments.filter(apt => {
              const query = apSearchQuery.toLowerCase();
              return (
                apt.fsm_service_appointment_number?.toLowerCase().includes(query) ||
                apt.client_contact_name?.toLowerCase().includes(query) ||
                apt.summary?.toLowerCase().includes(query) ||
                apt.service_address?.city?.toLowerCase().includes(query) ||
                apt.service_address?.full_address?.toLowerCase().includes(query) ||
                apt.fsm_work_order_number?.toLowerCase().includes(query)
              );
            })
          : appointments;
        
        if (loading) {
          return (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          );
        }
        
        if (filteredAppointments.length === 0) {
          return (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                <Calendar className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">
                  {apSearchQuery ? 'Nenhum resultado para a pesquisa' : `Nenhum serviço ${filter === 'today' ? 'para hoje' : 'encontrado'}`}
                </p>
              </CardContent>
            </Card>
          );
        }
        
        return (
          <div className="space-y-2">
            {filteredAppointments.map(apt => (
              <Card
                key={apt.id}
                className="cursor-pointer hover:shadow-md transition-shadow active:bg-gray-50"
                onClick={() => openAppointment(apt)}
                data-testid={`appointment-card-${apt.id}`}
              >
                <CardContent className="p-3">
                  {/* Header: AP number + Status */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-bold text-base text-blue-600 truncate">
                        {apt.fsm_service_appointment_number}
                      </span>
                      <StatusBadge status={apt.status} />
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                  
                  {/* Time + Client - single row */}
                  <div className="flex items-center gap-3 text-sm text-gray-700 mb-1.5">
                    <span className="flex items-center gap-1 font-medium">
                      <Clock className="w-3.5 h-3.5 text-gray-500" />
                      {formatTime(apt.scheduled_start)}
                    </span>
                    <span className="flex items-center gap-1 truncate min-w-0">
                      <User className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                      <span className="truncate">{apt.client_contact_name || 'Cliente'}</span>
                    </span>
                  </div>
                  
                  {/* Location */}
                  <div className="flex items-center gap-1 text-sm text-gray-500 mb-1.5">
                    <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">
                      {apt.service_address?.city || apt.service_address?.full_address || 'Sem morada'}
                    </span>
                  </div>
                  
                  {/* Summary - 1 line max on mobile */}
                  {apt.summary && (
                    <p className="text-xs text-gray-600 line-clamp-1 mb-1.5">
                      {apt.summary}
                    </p>
                  )}
                  
                  {/* Badges row: SKU warning + Lead */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {apt.has_unmatched_parts && (
                      <Badge className="bg-orange-100 text-orange-800 text-[10px] px-1.5 py-0.5">
                        <AlertTriangle className="w-3 h-3 mr-0.5" />SKU
                      </Badge>
                    )}
                    {(() => {
                      const resources = apt.assigned_resources || [];
                      const lead = resources.find(r => r.is_lead);
                      const legacyName = apt.fsm_service_resource_name || apt.stockflow_user_name;
                      const techName = lead?.display_name || (resources[0]?.display_name) || legacyName;
                      const extraCount = resources.length > 1 ? resources.length - 1 : 0;
                      
                      if (!techName) return null;
                      
                      return (
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <User className="w-3 h-3" />
                          <span className="truncate max-w-[120px]">{techName}</span>
                          {lead && <span className="text-purple-600 font-medium">Lead</span>}
                          {extraCount > 0 && <span className="text-gray-400">+{extraCount}</span>}
                        </div>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        );
      })()}
    </div>
  );

  // ==========================================
  // RENDER DETAIL VIEW
  // ==========================================
  const renderDetailView = () => {
    if (!selectedAppointment) return null;
    
    const apt = selectedAppointment;
    const isEditable = apt.status !== 'closed';
    const hasActual = (apt.actual_services?.length > 0) || (apt.actual_parts?.length > 0);
    
    return (
      <div className="space-y-4" data-testid="appointment-detail">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              if (returnTo) {
                // Navigate back to the origin page (e.g., /invoicing)
                navigate(returnTo);
              } else {
                // Default: show list view (Os Meus Serviços)
                setView('list');
                setSelectedAppointment(null);
                setReturnTo(null);
              }
            }}
            data-testid="ap-back-button"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">
              {apt.fsm_service_appointment_number}
            </h1>
          </div>
          <StatusBadge status={apt.status} />
        </div>

        {/* Planning changed warning */}
        {apt.planning_changed_after_confirmation && (
          <Card className="border-orange-200 bg-orange-50">
            <CardContent className="p-3 flex items-center gap-2 text-orange-800">
              <AlertTriangle className="w-5 h-5" />
              <span className="text-sm">Planeamento alterado após confirmação</span>
            </CardContent>
          </Card>
        )}

        {/* SECTION 1: Appointment Info (read-only) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Informação do Serviço
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">WO:</span>
                <span className="ml-2 font-medium">{apt.fsm_work_order_number || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Estado:</span>
                <span className="ml-2">{apt.fsm_status || '-'}</span>
              </div>
            </div>
            
            <div className="border-t pt-3">
              <div className="flex items-start gap-2 mb-2">
                <User className="w-4 h-4 mt-0.5 text-gray-400" />
                <div>
                  <div className="font-medium">{apt.client_contact_name || 'Cliente não definido'}</div>
                  {apt.client_company_name && (
                    <div className="text-sm text-gray-500 flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {apt.client_company_name}
                    </div>
                  )}
                </div>
              </div>
              
              {apt.client_phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <a href={`tel:${apt.client_phone}`} className="text-blue-600 hover:underline">
                    {apt.client_phone}
                  </a>
                </div>
              )}
              
              {apt.client_email && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <a href={`mailto:${apt.client_email}`} className="text-blue-600 hover:underline">
                    {apt.client_email}
                  </a>
                </div>
              )}
            </div>
            
            <div className="border-t pt-3">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 text-gray-400" />
                <div className="text-sm">
                  {apt.service_address?.full_address || 'Morada não definida'}
                </div>
              </div>
            </div>
            
            <div className="border-t pt-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-gray-400" />
                <span>{formatDateTime(apt.scheduled_start)}</span>
                {apt.scheduled_end && (
                  <>
                    <span className="text-gray-400">→</span>
                    <span>{formatTime(apt.scheduled_end)}</span>
                  </>
                )}
              </div>
            </div>
            
            {apt.summary && (
              <div className="border-t pt-3">
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {apt.summary}
                </div>
              </div>
            )}
            
            {/* Técnicos Atribuídos */}
            {(apt.assigned_resources?.length > 0 || apt.stockflow_user_name) && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Técnicos Atribuídos</span>
                </div>
                <div className="space-y-1 pl-6">
                  {apt.assigned_resources?.length > 0 ? (
                    apt.assigned_resources.map((resource, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {resource.is_lead ? (
                          <Crown className="w-3.5 h-3.5 text-purple-600" />
                        ) : (
                          <UserCheck className="w-3.5 h-3.5 text-blue-500" />
                        )}
                        <span className={resource.is_lead ? "font-medium" : ""}>
                          {resource.display_name}
                        </span>
                        {resource.is_lead && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs px-1.5 py-0">Lead</Badge>
                        )}
                        {resource.mapping_status === 'unmapped' && (
                          <Badge className="bg-orange-100 text-orange-700 text-xs px-1.5 py-0">
                            <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />Não mapeado
                          </Badge>
                        )}
                        {resource.stockflow_user_id === user?.id && (
                          <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0">Você</Badge>
                        )}
                      </div>
                    ))
                  ) : (
                    // Legacy single-user display
                    <div className="flex items-center gap-2 text-sm">
                      <UserCheck className="w-3.5 h-3.5 text-blue-500" />
                      <span>{apt.stockflow_user_name || apt.fsm_service_resource_name || 'Técnico'}</span>
                      {apt.stockflow_user_id === user?.id && (
                        <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0">Você</Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* User close permission indicator */}
            {apt.status !== 'closed' && apt._user_can_close === false && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 text-sm text-orange-700 bg-orange-50 p-2 rounded">
                  <AlertTriangle className="w-4 h-4" />
                  <span>Apenas o responsável principal pode concluir este serviço.</span>
                </div>
              </div>
            )}
            
            {/* MB Receipt section - only for closed appointments with MB payment */}
            {apt.status === 'closed' && (apt.payment_method === 'card' || apt.payment_method === 'MB') && (
              <div className="border-t pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-700">Talão Multibanco</span>
                </div>
                
                {apt.mb_receipt ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span className="text-sm text-green-700 font-medium">Talão anexado</span>
                      {apt.mb_receipt.verified && (
                        <Badge className="bg-green-100 text-green-800 text-xs">Verificado</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Enviado por {apt.mb_receipt.uploaded_by_name} em {new Date(apt.mb_receipt.uploaded_at).toLocaleString('pt-PT')}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          // Create a blob and open in new tab (works better than data URL for large images)
                          try {
                            const byteCharacters = atob(apt.mb_receipt.image_base64);
                            const byteNumbers = new Array(byteCharacters.length);
                            for (let i = 0; i < byteCharacters.length; i++) {
                              byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }
                            const byteArray = new Uint8Array(byteNumbers);
                            const blob = new Blob([byteArray], { type: apt.mb_receipt.content_type });
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (e) {
                            console.error('Error opening image:', e);
                            toast.error('Erro ao abrir imagem');
                          }
                        }}
                      >
                        <ZoomIn className="w-3 h-3 mr-1" />
                        Ver Talão
                      </Button>
                      {/* Delete button - only if not verified OR user is admin */}
                      {(!apt.mb_receipt.verified || user?.role === 'admin') && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={deleteMBReceipt}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Eliminar
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm text-amber-700 mb-2">
                      Tira uma foto do talão do Multibanco para a assistente de BO poder verificar o pagamento e emitir a fatura.
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      ref={mbReceiptInputRef}
                      className="hidden"
                      onChange={handleMBReceiptFileSelect}
                    />
                    <Button 
                      variant="default" 
                      size="sm"
                      onClick={() => mbReceiptInputRef.current?.click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Fotografar Talão MB
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 2: Planned Services */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Wrench className="w-4 h-4" />
                Serviços Previstos ({apt.planned_services?.length || 0})
              </span>
              {isEditable && (
                <Button variant="outline" size="sm" onClick={() => openAddItem('service')} data-testid="add-service-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apt.planned_services?.length > 0 ? (
              <div className="space-y-2">
                {apt.planned_services.map((service) => {
                  const isConfirmed = apt.actual_services?.some(
                    a => a.source_fsm_service_line_item_id === service.fsm_service_line_item_id
                  );
                  
                  return (
                    <div
                      key={service.fsm_service_line_item_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        isConfirmed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {isEditable ? (
                        <Checkbox
                          data-testid={`service-checkbox-${service.fsm_service_line_item_id}`}
                          checked={selectedServices[service.fsm_service_line_item_id] || false}
                          onCheckedChange={(checked) => {
                            setSelectedServices(prev => ({
                              ...prev,
                              [service.fsm_service_line_item_id]: checked
                            }));
                          }}
                        />
                      ) : isConfirmed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : null}
                      
                      <div className="flex-1">
                        <div className="font-medium text-sm">{service.name}</div>
                        {service.description && (
                          <div className="text-xs text-gray-500">{service.description}</div>
                        )}
                      </div>
                      
                      <div className="text-right">
                        {isEditable ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                const current = serviceQuantities[service.fsm_service_line_item_id] || 1;
                                if (current > 1) {
                                  setServiceQuantities(prev => ({
                                    ...prev,
                                    [service.fsm_service_line_item_id]: current - 1
                                  }));
                                }
                              }}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-8 text-center text-sm font-medium">
                              {serviceQuantities[service.fsm_service_line_item_id] || service.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                const current = serviceQuantities[service.fsm_service_line_item_id] || service.quantity;
                                setServiceQuantities(prev => ({
                                  ...prev,
                                  [service.fsm_service_line_item_id]: current + 1
                                }));
                              }}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-600">×{service.quantity}</span>
                        )}
                        <div className="text-sm font-medium">
                          €{calculateLineAmount(
                            service, 
                            isEditable 
                              ? (serviceQuantities[service.fsm_service_line_item_id] || service.quantity) 
                              : service.quantity
                          ).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Nenhum serviço previsto
              </p>
            )}
            {apt.actual_services?.some(sv => sv.source === 'technician_added') && (
              <div className="mt-4 pt-4 border-t" data-testid="tech-added-services">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Serviços Adicionados pelo Técnico</h4>
                {apt.actual_services.filter(sv => sv.source === 'technician_added').map(item => (
                  <TechItemRow key={item.id} item={item} isEditable={isEditable}
                    onQty={updateTechnicianItemQty} onRemove={removeTechnicianItem} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 3: Planned Parts */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Material Previsto ({apt.planned_parts?.length || 0})
              </span>
              {isEditable && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openAddItem('part')}
                  data-testid="add-part-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apt.planned_parts?.length > 0 ? (
              <div className="space-y-2">
                {apt.planned_parts.map((part) => {
                  const actualPart = apt.actual_parts?.find(
                    a => a.source_fsm_part_line_item_id === part.fsm_part_line_item_id
                  );
                  const isConfirmed = !!actualPart;
                  const isConfirmedNotUsed = actualPart && (actualPart.quantity === 0 || actualPart.confirmed_not_used);
                  const currentQty = partQuantities[part.fsm_part_line_item_id] ?? (actualPart?.quantity ?? part.quantity);
                  const isNotUsed = currentQty === 0 || !selectedParts[part.fsm_part_line_item_id];
                  
                  return (
                    <div
                      key={part.fsm_part_line_item_id}
                      className={`flex items-center gap-3 p-3 rounded-lg border ${
                        isConfirmedNotUsed ? 'bg-gray-100 border-gray-300 opacity-70' :
                        isConfirmed ? 'bg-green-50 border-green-200' :
                        part.match_status !== 'matched' ? 'bg-orange-50 border-orange-200' :
                        'bg-gray-50 border-gray-200'
                      }`}
                    >
                      {isEditable && part.match_status === 'matched' ? (
                        <Checkbox
                          checked={selectedParts[part.fsm_part_line_item_id] || false}
                          onCheckedChange={(checked) => {
                            setSelectedParts(prev => ({
                              ...prev,
                              [part.fsm_part_line_item_id]: checked
                            }));
                            // If unchecking, set quantity to 0
                            if (!checked) {
                              setPartQuantities(prev => ({
                                ...prev,
                                [part.fsm_part_line_item_id]: 0
                              }));
                            } else {
                              // If checking, restore original quantity
                              setPartQuantities(prev => ({
                                ...prev,
                                [part.fsm_part_line_item_id]: part.quantity
                              }));
                            }
                          }}
                        />
                      ) : isConfirmedNotUsed ? (
                        <XCircle className="w-5 h-5 text-gray-400" />
                      ) : isConfirmed ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                      ) : (
                        <div className="w-5" />
                      )}
                      
                      <div className="flex-1">
                        <div className="font-medium text-sm">{part.name}</div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">
                            {part.stockflow_sku || part.fsm_sku || 'Sem SKU'}
                          </span>
                          <MatchBadge status={part.match_status} />
                          {isConfirmedNotUsed && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              Não utilizado
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        {isEditable && part.match_status === 'matched' ? (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                const current = partQuantities[part.fsm_part_line_item_id] ?? part.quantity;
                                if (current >= 1) {
                                  const newQty = current - 1;
                                  setPartQuantities(prev => ({
                                    ...prev,
                                    [part.fsm_part_line_item_id]: newQty
                                  }));
                                  // If quantity becomes 0, uncheck the part
                                  if (newQty === 0) {
                                    setSelectedParts(prev => ({
                                      ...prev,
                                      [part.fsm_part_line_item_id]: false
                                    }));
                                  }
                                }
                              }}
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="w-8 text-center text-sm font-medium">
                              {partQuantities[part.fsm_part_line_item_id] ?? part.quantity}
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={() => {
                                const current = partQuantities[part.fsm_part_line_item_id] ?? part.quantity;
                                const newQty = current + 1;
                                setPartQuantities(prev => ({
                                  ...prev,
                                  [part.fsm_part_line_item_id]: newQty
                                }));
                                // If increasing from 0, also select the part
                                if (current === 0) {
                                  setSelectedParts(prev => ({
                                    ...prev,
                                    [part.fsm_part_line_item_id]: true
                                  }));
                                }
                              }}
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-600">×{part.quantity}</span>
                        )}
                        <div className="text-sm font-medium">
                          €{calculateLineAmount(
                            part,
                            isEditable
                              ? (partQuantities[part.fsm_part_line_item_id] || part.quantity)
                              : part.quantity
                          ).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">
                Nenhum material previsto
              </p>
            )}

            {/* Technician-added parts */}
            {apt.actual_parts?.filter(p => p.source === 'technician_added').length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Material Adicionado pelo Técnico</h4>
                {apt.actual_parts.filter(p => p.source === 'technician_added').map(item => (
                  <TechItemRow key={item.id} item={item} isEditable={isEditable}
                    onQty={updateTechnicianItemQty} onRemove={removeTechnicianItem} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action buttons */}
        {isEditable && (
          <Card className="sticky bottom-0 bg-white shadow-lg">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-3">
                {!hasPlannedConfirmation(apt) ? (
                  <Button
                    className="flex-1"
                    onClick={confirmAllItems}
                    disabled={confirmingItems}
                  >
                    {confirmingItems ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4 mr-2" />
                    )}
                    Confirmar Tudo
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={confirmSelectedItems}
                      disabled={confirmingItems}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Guardar Alterações
                    </Button>
                    <Button
                      className="flex-1"
                      onClick={() => closeAppointment()}
                      disabled={apt._user_can_close === false}
                      title={apt._user_can_close === false ? "Apenas o Lead pode concluir" : ""}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Fechar Serviço
                    </Button>
                  </>
                )}
              </div>
              
              <div className="flex justify-between text-sm mt-3 pt-3 border-t">
                <span className="text-gray-500">Total Previsto:</span>
                <span className="font-bold">€{apt.planned_total?.toFixed(2) || '0.00'}</span>
              </div>
              {isEditable && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Selecionado:</span>
                  <span className="font-bold text-blue-600">€{calculatePendingTotal().toFixed(2)}</span>
                </div>
              )}
              {hasActual && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Total Realizado:</span>
                  <span className="font-bold text-green-600">€{apt.actual_total?.toFixed(2) || '0.00'}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* SECTION: Financial Summary & Payment (for closed APs - useful for BO) */}
        {apt.status === 'closed' && (
          <Card data-testid="ap-financial-summary">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Resumo Financeiro
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Financial breakdown */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Materiais:</span>
                  <span className="font-medium">€{(apt.actual_parts_total || apt.planned_parts_total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Serviços / Mão de obra:</span>
                  <span className="font-medium">€{(apt.actual_services_total || apt.planned_services_total || 0).toFixed(2)}</span>
                </div>
                {(apt.travel_cost || apt.displacement_cost) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Deslocação:</span>
                    <span className="font-medium">€{(apt.travel_cost || apt.displacement_cost || 0).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t font-semibold">
                  <span>Total:</span>
                  <span className="text-green-600">€{(apt.actual_total || apt.planned_total || 0).toFixed(2)}</span>
                </div>
              </div>

              {/* Payment info */}
              <div className="pt-3 border-t space-y-2" data-testid="ap-payment-info">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Estado Pagamento:</span>
                  <Badge className={apt.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {apt.payment_status === 'paid' ? 'Pago' : 'Pendente'}
                  </Badge>
                </div>
                {apt.payment_method && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Método:</span>
                    <span className="font-medium">
                      {apt.payment_method === 'cash' || apt.payment_method === 'dinheiro' ? 'Dinheiro' :
                       apt.payment_method === 'card' || apt.payment_method === 'MB' ? 'MB' :
                       apt.payment_method === 'mbway' ? 'MBWay' :
                       apt.payment_method === 'transfer' || apt.payment_method === 'transferencia' ? 'Transferência' :
                       apt.payment_method}
                    </span>
                  </div>
                )}
              </div>
              <FsmPushStatus apt={apt} isAdmin={user?.role === 'admin'} onRepush={repushToFsm} />
            </CardContent>
          </Card>
        )}

        {/* Add Item Dialog */}
        <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{addItemType === 'service' ? 'Adicionar Serviço' : 'Adicionar Material'}</DialogTitle>
              <DialogDescription>
                {addItemType === 'service' ? 'Pesquise o serviço no catálogo por código ou nome' : 'Pesquise por SKU, nome, ou use o leitor de código de barras'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {addItemType === 'part' && (<>
              {/* Scanner button */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => {
                  setShowAddItemDialog(false);
                  setShowScanner(true);
                }}
                disabled={scannerLoading}
              >
                {scannerLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                Ler Código de Barras
              </Button>
              
              <div className="relative flex items-center">
                <div className="flex-1 border-t border-gray-200"></div>
                <span className="px-3 text-xs text-gray-500">ou pesquise</span>
                <div className="flex-1 border-t border-gray-200"></div>
              </div>
              </>)}
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  data-testid="add-item-search-input"
                  placeholder={addItemType === 'service' ? 'Pesquisar serviço por código ou nome...' : 'Pesquisar por SKU ou nome...'}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchProducts(e.target.value);
                  }}
                  className="pl-10"
                />
              </div>
              
              <div className="max-h-64 overflow-y-auto space-y-2">
                {searchLoading ? (
                  <div className="text-center py-4">
                    <RefreshCw className="w-5 h-5 animate-spin mx-auto text-gray-400" />
                  </div>
                ) : searchResults.length > 0 ? (
                  searchResults.map(product => {
                    const stockInWarehouse = (product.stock_by_warehouse || {})[user?.reference_warehouse] || 0;
                    const productPrice = product.sale_price_with_vat || product.sale_price || product.unit_price || 0;
                    return (
                      <div
                        key={product.sku}
                        className="flex items-center justify-between p-3 border rounded hover:bg-gray-50 cursor-pointer"
                        onClick={() => addTechnicianItem(product)}
                        data-testid={`product-result-${product.sku}`}
                      >
                        <div className="flex-1">
                          <div className="font-medium text-sm">{product.name}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-gray-500">{product.sku}</span>
                            {addItemType === 'part' && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                Stock: {stockInWarehouse}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <div className="text-sm font-medium text-blue-600">€{productPrice.toFixed(2)}</div>
                          <Plus className="w-5 h-5 text-blue-600 mx-auto" />
                        </div>
                      </div>
                    );
                  })
                ) : searchQuery.length >= 2 ? (
                  <p className="text-center text-gray-500 py-4">Nenhum resultado</p>
                ) : (
                  <p className="text-center text-gray-400 py-4 text-sm">
                    Digite pelo menos 2 caracteres
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
                Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Barcode Scanner */}
        {showScanner && (
          <FullscreenScanner
            onScan={handleBarcodeScan}
            onClose={() => setShowScanner(false)}
          />
        )}

        {/* Close Appointment Dialog */}
        {/* MB Receipt Preview Dialog */}
        <Dialog open={mbReceiptPreview !== null} onOpenChange={(open) => !open && setMBReceiptPreview(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Confirmar Talão MB</DialogTitle>
              <DialogDescription>
                Verifica se a foto está legível antes de enviar
              </DialogDescription>
            </DialogHeader>
            
            {mbReceiptPreview && (
              <div className="space-y-4">
                <div className="border rounded-lg overflow-hidden bg-gray-50">
                  <img 
                    src={mbReceiptPreview.dataUrl} 
                    alt="Preview do talão MB" 
                    className="w-full h-auto max-h-64 object-contain"
                  />
                </div>
                <p className="text-sm text-gray-500 text-center">
                  Ficheiro: {mbReceiptPreview.file.name}
                </p>
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setMBReceiptPreview(null);
                  if (mbReceiptInputRef.current) mbReceiptInputRef.current.value = '';
                }}
                disabled={mbReceiptUploading}
              >
                Cancelar
              </Button>
              <Button 
                onClick={() => uploadMBReceipt(mbReceiptPreview?.file)}
                disabled={mbReceiptUploading || !mbReceiptPreview}
              >
                {mbReceiptUploading ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Enviar Talão
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Close Appointment Dialog */}
        <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Concluir Serviço</DialogTitle>
              <DialogDescription>
                Preenche os dados de pagamento e faturação
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Payment Section */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Estado do Pagamento
                </label>
                <div className="flex gap-2">
                  <Button
                    variant={closeData.payment_status === 'pending' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCloseData(prev => ({ ...prev, payment_status: 'pending' }))}
                  >
                    Pendente
                  </Button>
                  <Button
                    variant={closeData.payment_status === 'paid' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCloseData(prev => ({ ...prev, payment_status: 'paid' }))}
                  >
                    Pago
                  </Button>
                </div>
              </div>
              
              {closeData.payment_status === 'paid' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">
                    Método de Pagamento *
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {['mbway', 'cash', 'card', 'transfer'].map(method => (
                      <Button
                        key={method}
                        variant={closeData.payment_method === method ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setCloseData(prev => ({ ...prev, payment_method: method }))}
                      >
                        {method === 'mbway' ? 'MBWay' : 
                         method === 'cash' ? 'Dinheiro' : 
                         method === 'card' ? 'MB' : 'Transf.'}
                      </Button>
                    ))}
                  </div>
                  
                  {/* MB Receipt Upload Section */}
                  {closeData.payment_method === 'card' && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <label className="text-sm font-medium text-blue-800 block mb-2">
                        <Camera className="w-4 h-4 inline mr-1" />
                        Talão Multibanco (opcional)
                      </label>
                      
                      {closeMbReceiptPreview ? (
                        <div className="relative">
                          <img 
                            src={closeMbReceiptPreview} 
                            alt="Talão MB" 
                            className="w-full max-h-40 object-contain rounded border"
                          />
                          <div className="flex gap-2 mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(closeMbReceiptPreview, '_blank')}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Ver
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                setCloseMbReceiptFile(null);
                                setCloseMbReceiptPreview(null);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remover
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            ref={closeMbReceiptInputRef}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                setCloseMbReceiptFile(file);
                                setCloseMbReceiptPreview(URL.createObjectURL(file));
                              }
                            }}
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            onClick={() => closeMbReceiptInputRef.current?.click()}
                          >
                            <Camera className="w-4 h-4 mr-2" />
                            Fotografar Talão
                          </Button>
                          <p className="text-xs text-blue-600 mt-1">
                            Pode adicionar depois na Informação do Serviço
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Invoice Section */}
              <div className="pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Checkbox
                    id="wants_invoice"
                    data-testid="close-wants-invoice-checkbox"
                    checked={closeData.wants_invoice}
                    onCheckedChange={(checked) => setCloseData(prev => ({ ...prev, wants_invoice: checked }))}
                  />
                  <label htmlFor="wants_invoice" className="text-sm font-medium">
                    Cliente pretende fatura
                  </label>
                </div>
                
                {closeData.wants_invoice && (
                  <div className="space-y-3 pl-6">
                    <div>
                      <label className="text-sm text-gray-700 block mb-1">NIF *</label>
                      <Input
                        placeholder="123456789"
                        maxLength={11}
                        value={closeData.client_nif}
                        onChange={(e) => setCloseData(prev => ({ ...prev, client_nif: e.target.value.replace(/[^\d\s]/g, '') }))}
                        data-testid="close-nif-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">9 dígitos do NIF português</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-700 block mb-1">Nome (Fatura) *</label>
                      <Input
                        placeholder="Nome do cliente"
                        value={closeData.client_name}
                        onChange={(e) => setCloseData(prev => ({ ...prev, client_name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-700 block mb-1">Email (envio) *</label>
                      <Input
                        type="email"
                        placeholder="email@exemplo.pt"
                        value={closeData.client_email}
                        onChange={(e) => setCloseData(prev => ({ ...prev, client_email: e.target.value }))}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Notes */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Observações
                </label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm"
                  rows={2}
                  placeholder="Notas sobre o serviço..."
                  value={closeData.notes}
                  onChange={(e) => setCloseData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </div>
            
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setShowCloseDialog(false)} disabled={closingAppointment}>
                Cancelar
              </Button>
              <Button onClick={submitCloseAppointment} disabled={closingAppointment}>
                {closingAppointment ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                Concluir Serviço
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  // ==========================================
  // MAIN RENDER
  // ==========================================
  
  // Deep-link loading state
  if (deepLinkLoading) {
    return (
      <div className="max-w-2xl mx-auto p-4 pb-24 flex flex-col items-center justify-center min-h-[300px]" data-testid="fsm-appointments-page">
        <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
        <p className="text-gray-600">A carregar AP...</p>
      </div>
    );
  }
  
  // Deep-link error state (show list with error message)
  if (deepLinkError && view === 'list') {
    return (
      <div className="max-w-2xl mx-auto p-4 pb-24" data-testid="fsm-appointments-page">
        <Card className="border-red-200 bg-red-50 mb-4">
          <CardContent className="p-4 flex items-center gap-3 text-red-700">
            <XCircle className="w-5 h-5" />
            <div>
              <p className="font-medium">{deepLinkError}</p>
              <p className="text-sm text-red-600">Verifique se tem permissão para visualizar esta AP ou se o ID está correcto.</p>
            </div>
          </CardContent>
        </Card>
        {renderListView()}
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto p-4 pb-24" data-testid="fsm-appointments-page">
      {view === 'list' ? renderListView() : renderDetailView()}
    </div>
  );
};

export default FSMServiceAppointments;
